import { useState } from 'react';

import { useAgencyConfiguration } from '@/app/shell/agencyConfiguration';
import type { Incident } from '@/signalr';
import type { IncidentAppleEedCapabilities } from '@/signalr';
import {
  fetchAppleEedAdditionalData,
  requestAppleEedMedia as requestAppleEedMediaApi,
  type AppleEedCallInformation,
  type AppleEedCallerInformation,
} from '@ngsos/portal-core/commands';
import { appleEedAdditionalDataHasExpired, canUseAppleEedMedicalData } from '@ngsos/portal-core/incidents';
import { AdditionalDataSection } from './sections/additional/AdditionalDataSection';
import { CallerSection } from './sections/caller/CallerSection';
import { CallerLocationSection } from './sections/caller-location/CallerLocationSection';
import { CallerDataSourcesPanel } from './sections/diagnostics/CallerDataSourcesPanel';
import { IncidentCallsPanel } from './sections/diagnostics/IncidentCallsPanel';
import { AppleEedExpiredSection } from './sections/apple-eed/AppleEedExpiredSection';
import { AppleEedSection } from './sections/apple-eed/AppleEedSection';
import { GoogleElsSection } from './sections/google-els/GoogleElsSection';
import { IncidentSection } from './sections/incident/IncidentSection';
import { MobileAppSection } from './sections/mobile-app/MobileAppSection';
import { PatientSection } from './sections/patient/PatientSection';
import { UploadedMediaSection } from './sections/uploaded-media/UploadedMediaSection';

export type IncidentDataPanelProps = {
  incident: Incident;
  incidentIsReadOnly: boolean;
  appleEedCallCapabilities: readonly IncidentAppleEedCapabilities[];
};

export function IncidentDataPanel({ incident, incidentIsReadOnly, appleEedCallCapabilities }: IncidentDataPanelProps) {
  const agencyConfiguration = useAgencyConfiguration();
  const [appleEedCallInformation, setAppleEedCallInformation] = useState<AppleEedCallInformation | null>(null);
  const [appleEedMedicalData, setAppleEedMedicalData] = useState<AppleEedCallerInformation | null>(null);
  const [appleEedBatteryLevel, setAppleEedBatteryLevel] = useState<string | null>(null);
  const [askedForAppleEedMedicalData, setAskedForAppleEedMedicalData] = useState(false);
  const [appleEedMedicalDataIsLoading, setAppleEedMedicalDataIsLoading] = useState(false);
  const [appleEedMediaRequested, setAppleEedMediaRequested] = useState(false);

  const appleEedExpiresAtUtc = incident.appleEedAdditionalDataExpiresAtUtc;
  const showAppleEedPanel = canUseAppleEedMedicalData(
    agencyConfiguration.appleEedAllowMedicalData,
    appleEedExpiresAtUtc,
  );
  const appleEedHasExpired = appleEedAdditionalDataHasExpired(appleEedExpiresAtUtc);

  const getAppleEedMedicalData = async () => {
    if (!showAppleEedPanel) {
      return;
    }

    const validPhoneNumber = appleEedCallCapabilities.find((capability) => capability.hasAdditionalData)?.phoneNumber;
    if (validPhoneNumber == null) {
      return;
    }

    setAppleEedMedicalDataIsLoading(true);
    try {
      const additionalData = await fetchAppleEedAdditionalData(validPhoneNumber);
      setAskedForAppleEedMedicalData(true);
      if (additionalData === null) {
        return;
      }
      setAppleEedCallInformation(additionalData.additionalData?.call_information ?? null);
      setAppleEedMedicalData(additionalData.additionalData?.caller_information ?? null);
      setAppleEedBatteryLevel(additionalData.batteryLevel ?? null);
    } finally {
      setAppleEedMedicalDataIsLoading(false);
    }
  };

  const requestAppleEedMedia = async () => {
    if (!showAppleEedPanel) {
      return;
    }

    const validPhoneNumber = appleEedCallCapabilities.find((capability) => capability.hasMediaUpload)?.phoneNumber;
    if (validPhoneNumber == null) {
      return;
    }

    await requestAppleEedMediaApi(incident.id, validPhoneNumber);
    setAppleEedMediaRequested(true);
  };

  return (
    <div className="d-flex flex-column gap-3">
      <IncidentSection
        incidentId={incident.id}
        incidentLabel={incident.additionalIncidentData.incidentLabel}
        purpose={incident.purpose}
        createdTimeUtc={incident.createdTimeUtc}
        closedTimeUtc={incident.closedTimeUtc}
        incidentIsReadOnly={incidentIsReadOnly}
        incidentInvolvedAgencyStates={incident.involvedAgencies}
        summary={incident.dispatcher.summary}
      />

      <PatientSection
        incidentId={incident.id}
        description={incident.patient.description}
        incidentIsReadOnly={incidentIsReadOnly}
      />

      <CallerSection incidentId={incident.id} caller={incident.caller} incidentIsReadOnly={incidentIsReadOnly} />

      <CallerLocationSection
        incidentId={incident.id}
        incidentIsReadOnly={incidentIsReadOnly}
        callerPosition={incident.lastPosition}
        callerPositionHistory={incident.positions}
        caller={incident.caller}
        chatIsActive={incident.chatId != null}
      />

      <MobileAppSection
        sources={incident.caller.dataSources.mobileApp}
        deviceInfo={incident.caller.deviceInfo}
        createdTimeUtc={incident.createdTimeUtc}
      />

      <GoogleElsSection sources={incident.caller.dataSources.googleEls} createdTimeUtc={incident.createdTimeUtc} />

      {appleEedExpiresAtUtc != null && appleEedHasExpired && <AppleEedExpiredSection expiredAtUtc={appleEedExpiresAtUtc} />}

      {showAppleEedPanel && (
        <AppleEedSection
          appleEedCallInformation={appleEedCallInformation}
          appleEedMedicalData={appleEedMedicalData}
          appleEedBatteryLevel={appleEedBatteryLevel}
          appleEedCallCapabilities={appleEedCallCapabilities}
          askedForAppleEedMedicalData={askedForAppleEedMedicalData}
          appleEedMedicalDataIsLoading={appleEedMedicalDataIsLoading}
          askedForAppleEedMedia={appleEedMediaRequested}
          getAppleEedMedicalData={getAppleEedMedicalData}
          requestAppleEedMedia={requestAppleEedMedia}
        />
      )}

      {incident.attachments.length > 0 && <UploadedMediaSection media={incident.attachments} />}

      <AdditionalDataSection
        incidentId={incident.id}
        additionalIncidentData={incident.additionalIncidentData}
        incidentIsReadOnly={incidentIsReadOnly}
      />

      <CallerDataSourcesPanel dataSources={incident.caller.dataSources} snapshotRevision={incident.snapshotRevision} />

      <IncidentCallsPanel involvedAgencies={incident.involvedAgencies} agencyViews={incident.agencyViews} />
    </div>
  );
}
